import type { Vec3 } from "./entityData";
import type { SkyboxData } from "./skyboxData";

/**
 * `SceneInfo` — public-facing scene metadata exposed by `IProjectModel`.
 * Field names and intent mirror the Unity-side `SceneInfo` view of
 * `SceneData` (see `Models/Data/SceneData.cs`). Only fields the UI shell
 * consumes today are kept; scene-mode lands later.
 */
export type SceneTransitionSpeed = 0 | 1 | 2 | 3 | 4;
export type HiddenObjectMode = "xray" | "hidden";

/**
 * Unity `ShadowMode` flags (`Models/Data/SceneData.cs:42`). Shadows are
 * persisted per device family; the web port reads/writes the `Desktop` bit.
 */
export const SHADOW_MODE = Object.freeze({
  None: 0,
  Desktop: 1 << 0,
  Tablet: 1 << 1,
  StandaloneHeadset: 1 << 2,
  TetheredHeadset: 1 << 3,
  All: (1 << 0) | (1 << 1) | (1 << 2) | (1 << 3),
} as const);

/** Web ↔ Unity desktop parity: the web port uses Unity's `Desktop` flag. */
export function isShadowVisible(shadowMask: number): boolean {
  return (shadowMask & SHADOW_MODE.Desktop) !== 0;
}

export function withShadowVisible(shadowMask: number, visible: boolean): number {
  return visible ? shadowMask | SHADOW_MODE.Desktop : shadowMask & ~SHADOW_MODE.Desktop;
}

/** Unity `SceneInfo.LightVisible` (`LightMode == 1`). */
export function isLightVisible(lightMode: number): boolean {
  return lightMode === 1;
}

/**
 * The five environment booleans of Unity's `SceneEnvironmentData`
 * (`Models/SceneModel/ISceneModel.cs:235`). `shadowsVisible` refers to the
 * current device family — the desktop bit on the web.
 */
export interface SceneEnvironmentPatch {
  readonly shadowsVisible: boolean;
  readonly gridVisible: boolean;
  readonly consoleVisible: boolean;
  readonly tableVisible: boolean;
  readonly lightVisible: boolean;
}

/**
 * Per-scene persisted cutting-plane batch (Unity `SceneData`
 * `cuttingPlaneVisible` / `cuttingPlaneExist` / `cuttingPlanePosition` /
 * `cuttingPlaneRotation` + `UsePartColor`, written together by
 * `DynamoClient.UpdateSceneCuttingPlane`).
 *
 * Pose is **scene-root-space, Unity axes**: `position` in meters, `euler` in
 * degrees (Unity `Quaternion.Euler` ZXY order), WITHOUT the 180°-about-Y
 * gizmo offset Unity bakes into the live plane GameObject. The kept
 * half-space is `dot(p - position, forwardOf(euler)) >= 0` (see
 * `models/tools/cuttingPlaneMath.ts`).
 */
export interface CuttingPlaneState {
  readonly visible: boolean;
  readonly exist: boolean;
  readonly position: Vec3;
  readonly euler: Vec3;
  /** Cut cross-section tint: false = Unity's red fallback, true = material base color. */
  readonly usePartColor: boolean;
}

export const DEFAULT_CUTTING_PLANE: CuttingPlaneState = Object.freeze({
  visible: false,
  exist: false,
  position: [0, 0, 0] as const,
  euler: [0, 0, 0] as const,
  usePartColor: false,
});

/**
 * Per-scene persisted camera view — Unity `SceneData.SceneViewPosition` /
 * `SceneViewRotation` / `IsViewOrthographic`, written together by
 * `DynamoClient.UpdateSceneView` (`DynamoClient.cs:2130`).
 *
 * ⚠ `pivot` is the ORBIT PIVOT, a world POSITION — despite riding the Dynamo
 * attribute named `SceneViewRotation`. Unity's `SceneInfo.SceneCameraEuler`
 * getter is the same lie one layer up. Proof is a closed loop in
 * `DesktopSceneModel`: the value written in is `m_sceneComponent.Pivot`
 * (`:294`), the value read out goes straight back to it (`:404`, `:503-508`),
 * and Battlehub's `Pivot` is literally `m_pivot.transform.position`
 * (`RuntimeSelectionComponent.cs:305`). A third consumer settles it —
 * `RenderPreview` (`:356`) feeds the same value to
 * `AssetPreviewUtility.SetCameraPose(Vector3 position, Vector3 lookAt)`,
 * which passes it to `Transform.LookAt`. Nothing anywhere converts it to a
 * rotation. **Never** run it through the euler/quaternion basis conversion —
 * it is a position and takes `convertBasisPosition`.
 *
 * Both vectors are **world-space, UNITY axes, metres**, exactly as the
 * desktop client persists them; `sceneViewCameraHost.ts` converts at the
 * renderer boundary (the same rule participant transforms follow). Storing
 * three-space here would make every web-authored view unreadable on desktop.
 *
 * The zoom level is NOT stored: it is implied by `|position - pivot|`, which
 * is how Battlehub maintains `m_orbitDistance`. Nor is an orthographic SIZE —
 * Unity has the same gap, so an orthographic view restores its projection
 * mode but not its zoom on the desktop client too.
 */
export interface SceneViewState {
  /** Camera world position. Unity `SceneData.SceneViewPosition`. */
  readonly position: Vec3;
  /** Orbit pivot world position. Unity `SceneData.SceneViewRotation` (sic). */
  readonly pivot: Vec3;
  /**
   * Unity `SceneData.IsViewOrthographic`. Round-tripped so a desktop-authored
   * flag survives a web write, but never APPLIED: `OrbitCameraController`
   * wraps a `PerspectiveCamera` and the port has no orthographic path.
   */
  readonly isOrthographic: boolean;
}

/** Unity's read defaults (`SafeGetVector3Attribute` → 0, `SafeGetBoolAttribute(..., false)`). */
export const UNSET_SCENE_VIEW: SceneViewState = Object.freeze({
  position: [0, 0, 0] as const,
  pivot: [0, 0, 0] as const,
  isOrthographic: false,
});

/**
 * Battlehub `MathHelper.Approximately(Vector3, Vector3)`
 * (`MathHelper.cs:14`): `sqrMagnitude(a - b) <= 0.01²`, i.e. a 1 cm ball.
 * Squared, so no `sqrt` — same as upstream.
 */
const K_APPROX_EPSILON_SQ = 0.01 * 0.01;

export function vec3Approximately(a: Vec3, b: Vec3): boolean {
  const dx = a[0] - b[0];
  const dy = a[1] - b[1];
  const dz = a[2] - b[2];
  return dx * dx + dy * dy + dz * dz <= K_APPROX_EPSILON_SQ;
}

/**
 * Port of `DesktopSceneModel.IsSceneViewSet` (`:379`): a scene has an
 * authored view when the camera position OR the pivot is away from the
 * origin, OR the view is orthographic.
 *
 * ⚠ Do NOT "unify" this with the inline check inside Unity's `GetSceneView`
 * (`:398-402`), which is a DIFFERENT predicate — `(pos != 0 AND pivot != 0)
 * OR ortho`, with exact equality rather than the 1 cm tolerance. The two can
 * disagree (a view whose pivot is exactly the origin reads set here and unset
 * there), and each has its own call sites upstream. See
 * {@link isSceneViewResolvable}.
 */
export function isSceneViewSet(view: SceneViewState): boolean {
  return (
    !vec3Approximately(view.position, [0, 0, 0])
    || !vec3Approximately(view.pivot, [0, 0, 0])
    || view.isOrthographic
  );
}

/**
 * The OTHER predicate — the inline check inside `DesktopSceneModel.GetSceneView`
 * (`:398-402`): `(SceneCameraPosition != zero && SceneCameraEuler != zero)
 * || IsViewOrthographic`, using exact `Vector3 !=` (which is itself Unity's
 * 1e-5-ish `==` operator, not a 1 cm ball). Kept separate on purpose — see
 * the warning on {@link isSceneViewSet}.
 */
export function isSceneViewResolvable(view: SceneViewState): boolean {
  const posSet = !vec3IsZeroExact(view.position);
  const pivotSet = !vec3IsZeroExact(view.pivot);
  return (posSet && pivotSet) || view.isOrthographic;
}

function vec3IsZeroExact(v: Vec3): boolean {
  return v[0] === 0 && v[1] === 0 && v[2] === 0;
}

/**
 * Port of the comparison inside `DesktopSceneModel.IsCameraMoved` (`:376`):
 * both vectors within 1 cm and the same projection mode.
 */
export function sceneViewApproximately(a: SceneViewState, b: SceneViewState): boolean {
  return (
    vec3Approximately(a.position, b.position)
    && vec3Approximately(a.pivot, b.pivot)
    && a.isOrthographic === b.isOrthographic
  );
}

export const SCENE_TRANSITION_SPEED = Object.freeze({
  None: 0,
  Quick: 1,
  Slow: 2,
  VerySlow: 3,
  VeryQuick: 4,
} satisfies Record<string, SceneTransitionSpeed>);

export interface SceneInfo {
  readonly id: string;
  readonly name: string;
  /** 1-based ordering index used by the scenes list label ("1. Overview"). */
  readonly index: number;
  /** URL to the scene's preview image, or `null` while it's still loading
   * or the scene has never been opened. Real thumbnails arrive in M6. */
  readonly thumbnailUrl: string | null;
  /** Cached count of top-level entities for quick display. */
  readonly entityCount: number;
  /** ISO-8601 timestamp (`SceneData.ModifiedTimestamp`). */
  readonly modifiedAt: string;
  /** Unity `SceneData.PresenterNotes`, shown in the scene inspector notes field. */
  readonly presenterNotes: string;
  /** Unity `SceneData.AssetIDs`, used to decide scene-transition reuse. */
  readonly assetIds: ReadonlyArray<string>;
  /** Unity `SceneData.MediaAssetIDs` — media-tile content ids in this scene. */
  readonly mediaAssetIds: ReadonlyArray<string>;
  /** Unity `SceneData.TransitionSpeed`; default is Quick. */
  readonly transitionSpeed: SceneTransitionSpeed;
  /** Unity `SceneData.ShowHiddenAs`; XRay is Unity's default. */
  readonly hiddenObjectMode: HiddenObjectMode;
  /** Unity `SceneData.IsolatedEntityIDs`; empty means isolation mode is off. */
  readonly isolatedSnapshotIds: ReadonlyArray<string>;

  // -- Environment (Unity `SceneData` flat fields; persisted via
  //    `DynamoClient.UpdateSceneEnvironment`, `DynamoClient.cs:2368`) --

  /** Unity `SceneData.GridVisible`; default false. */
  readonly gridVisible: boolean;
  /** Unity `SceneData.ShadowsVisible` (`ShadowMode` flags; Dynamo
   * `ShadowsVisiblePerDevice`). Use {@link isShadowVisible} for the
   * desktop-family bool the UI binds to. */
  readonly shadowsVisibleMask: number;
  /** Unity `SceneData.ConsoleVisible`; default derives from skybox mode. */
  readonly consoleVisible: boolean;
  /** Unity `SceneData.TableVisible`; default derives from skybox mode. */
  readonly tableVisible: boolean;
  /** Unity `SceneData.LightMode` (1 = default lights on). */
  readonly lightMode: number;

  /** Unity `SceneData.Skybox` (persisted via `UpdateSceneSkybox`). */
  readonly skybox: SkyboxData;

  /** Cutting-plane batch (persisted via `UpdateSceneCuttingPlane`). */
  readonly cuttingPlane: CuttingPlaneState;

  /**
   * Unity `SceneData.ShouldSnapback` (default true) — the Grab tool's
   * per-scene "Snap back" toggle (persisted via `UpdateSceneSnapback`,
   * `DynamoClient.cs:2398`: `SET #SB = :sb`, no `ModifiedTimestamp` touch).
   */
  readonly shouldSnapback: boolean;

  /**
   * Unity `SceneData.SceneViewPosition` / `SceneViewRotation` /
   * `IsViewOrthographic` — the scene's authored camera view, persisted via
   * `UpdateSceneView` (`DynamoClient.cs:2130`: `SET #SP = :sp, #SR = :sr,
   * #VO = :vo`, which deliberately leaves `ModifiedTimestamp` alone).
   *
   * SHARED per scene, not per user: it lives on the scene row, rides the
   * full-`SceneData` `update_scene` broadcast, and Unity's Copy View / Paste
   * View menu moves it between scenes verbatim.
   *
   * See {@link SceneViewState} — `pivot` is a POSITION, not a rotation.
   */
  readonly sceneView: SceneViewState;
}
